import { collectPrintSelection, buildMergedLabelsHtmlAsync } from '@onward-delivery/core';
import { alignManifestForItemLabelPrinting, enrichManifest } from './enrichManifest';
import { buildGroupedManifestItemsAndCounts } from './manifestPrintGrouping';

// Renders label HTML in a hidden 4x6 iframe and invokes the OS print dialog. Resolves once the
// dialog closes (afterprint), with a timeout fallback for browsers that don't fire it in an iframe.
export async function printHtmlLabelsViaIframe(html) {
    const iframe = document.createElement('iframe');
    iframe.setAttribute(
        'style',
        'position:fixed;left:-10000px;top:0;width:4in;height:6in;border:0;pointer-events:none;'
    );
    document.body.appendChild(iframe);

    const cleanup = () => {
        if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
        }
    };

    try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();

        const printStyle = doc.createElement('style');
        printStyle.textContent = `
            @page { size: 8.5in 11in; margin: 0; }
            @media print {
                html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
                .label-page {
                    width: 4in !important;
                    height: 6in !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    box-sizing: border-box !important;
                    page-break-after: always !important;
                    break-after: page !important;
                }
                .label-page:last-of-type {
                    page-break-after: auto !important;
                    break-after: auto !important;
                }
            }
        `;
        doc.head.appendChild(printStyle);

        const images = Array.from(doc.images || []);
        await Promise.all(
            images.map((img) =>
                img.complete
                    ? Promise.resolve()
                    : new Promise((resolve) => {
                          img.onload = resolve;
                          img.onerror = resolve;
                      })
            )
        );

        // Let layout flush before invoking print.
        await new Promise((resolve) => requestAnimationFrame(() => resolve()));

        const printWindow = iframe.contentWindow;
        await new Promise((resolve) => {
            let timerId;
            const finish = () => {
                if (timerId) clearTimeout(timerId);
                resolve();
            };
            // afterprint fires once the OS print dialog closes (accepted or cancelled).
            // Some browsers don't reliably fire it from inside an iframe — fall back to a timeout.
            printWindow.addEventListener('afterprint', finish, { once: true });
            timerId = setTimeout(finish, 60000);
            printWindow.focus();
            printWindow.print();
        });
    } finally {
        cleanup();
    }
}

// Tick the grouped entries for one label kind: 'items' = one label per item (loose or on a pallet),
// 'pallets' = one label per pallet, ignoring loose items since they have no pallet label.
const selectionFor = (groupedItems, kind) =>
    Object.fromEntries(
        Object.entries(groupedItems || {}).map(([key, entry]) =>
            entry.isPallet
                ? [
                      key,
                      {
                          ...entry,
                          checked: kind === 'pallets',
                          itemChecks: Object.fromEntries(entry.items.map((_, i) => [i, kind === 'items'])),
                      },
                  ]
                : [key, { ...entry, checked: kind === 'items' }]
        )
    );

// Print one kind of label for everything on `manifest` — no dialog. Callers narrow the manifest to
// the user's grid selection first, so everything present is meant to print. Returns false when the
// selection yields no labels of that kind (e.g. pallet labels for items that aren't palletized).
async function printManifestLabels(manifest, kind) {
    const { groupedItems } = buildGroupedManifestItemsAndCounts(alignManifestForItemLabelPrinting(manifest));
    const { itemsToPrint, palletsToPrint } = collectPrintSelection(selectionFor(groupedItems, kind));
    if (!itemsToPrint.length && !palletsToPrint.length) return false;

    const html = await buildMergedLabelsHtmlAsync({ itemsToPrint, palletsToPrint });
    await printHtmlLabelsViaIframe(html);
    return true;
}

export const printManifestItemLabels = (manifest) => printManifestLabels(manifest, 'items');
export const printManifestPalletLabels = (manifest) => printManifestLabels(manifest, 'pallets');

// Print explicit item and pallet selections. Unlike the manifest-grid helpers above, this accepts
// IDs because Cycle Count fetches the full inbound manifest before the user selects what to print.
export async function printLabelSelection({ manifest, itemIds = [], palletIds = [] }) {
    if (!manifest) return;
    const targetItemIds = new Set((itemIds || []).map(String));
    const targetPalletIds = new Set((palletIds || []).map(String));

    // GET_MANIFEST returns `items` (join rows) but no `orders`; enrichManifest derives them.
    const enriched = enrichManifest(manifest);
    const aligned = alignManifestForItemLabelPrinting(enriched);
    const { groupedItems } = buildGroupedManifestItemsAndCounts(aligned);

    Object.values(groupedItems || {}).forEach((group) => {
        if (group.isPallet) {
            const palletId = group.items?.[0]?.pallet?.pallet_id;
            if (palletId && targetPalletIds.has(String(palletId))) {
                group.checked = true;
            }
            // itemChecks keys are indexes into group.items, not item_ids.
            const itemChecks = { ...(group.itemChecks || {}) };
            (group.items || []).forEach((item, index) => {
                if (item?.item_id && targetItemIds.has(String(item.item_id))) {
                    itemChecks[index] = true;
                }
            });
            group.itemChecks = itemChecks;
        } else {
            const itemId = group.items?.item_id;
            if (itemId && targetItemIds.has(String(itemId))) {
                group.checked = true;
            }
        }
    });

    const { itemsToPrint, palletsToPrint } = collectPrintSelection(groupedItems);
    if ((!itemsToPrint || itemsToPrint.length === 0) && (!palletsToPrint || palletsToPrint.length === 0)) {
        return false;
    }
    const html = await buildMergedLabelsHtmlAsync({ itemsToPrint, palletsToPrint });
    await printHtmlLabelsViaIframe(html);
    return true;
}
